// Admin Fury Lab: time slots, blocks with coaches, assign people, send initial/reminder emails.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  deleteDoc,
  onSnapshot,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { firestore } from '../firebase';
import { getActiveEventId, resolveAdminDefaultEventId } from '../utils/activeEvent';
import {
  deriveBlocksFromTimeSlots,
  assignmentDocId,
  buildBlockSummary,
  formatEventDate,
  mergeFuryLabParticipantsFromTickets,
  furyLabParticipantsListsEqual,
} from '../utils/furyLabUtils';
import { getFuryLabDateLabel } from '../utils/eventUtils';
import { getDisplayName } from '../utils/flyerDisplay';
import { formatHeight, formatWeight } from '../utils/heightWeightUtils';
import { openPrintWindow, getOperationalTablePrintCss } from '../utils/printUtils';
import { copyText, buildTsvTable } from '../utils/clipboardUtils';
import AdminCopyMenu from './admin/AdminCopyMenu';
import AdminColumnExportModal from './admin/AdminColumnExportModal';
import EmailComposerModal from './EmailComposerModal';
import { useEmailComposer } from '../hooks/useEmailComposer';
import './FuryLab.css';

function escapeHtmlCell(s) {
  if (s == null) return '';
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const FURY_SCHEDULE_EXPORT_COLS = [
  { id: 'timeSlot', label: 'Time slot', default: true },
  { id: 'meetTime', label: 'Meet time', default: true },
  { id: 'coach', label: 'Coach', default: true },
  { id: 'flying', label: 'Flying', default: true },
];

const FURY_PARTICIPANT_EXPORT_COLS = [
  { id: 'name', label: 'Name', default: true },
  { id: 'email', label: 'Email', default: false },
  { id: 'phone', label: 'Phone', default: true },
  { id: 'meetTime', label: 'Meet time', default: true },
  { id: 'assignedTimes', label: 'Assigned times', default: true },
  { id: 'coaches', label: 'Coaches', default: true },
  { id: 'waterCanopyThu', label: 'Water/Canopy Thu', default: true },
  { id: 'ht', label: 'Ht', default: false },
  { id: 'wt', label: 'Wt', default: false },
  { id: 'jumps', label: 'Jumps', default: true },
  { id: 'tunnel', label: 'Tunnel', default: false },
  { id: 'emailStatus', label: 'Emails (initial | reminder)', default: false },
];

/** Sub-event label for Fury Lab in event manifest / ticket data. */
const FURY_LAB_SUB_EVENT_LABEL = 'Fury Lab';

function participantIdsRemovedFromRoster(prevParticipants, nextParticipants) {
  const nextIds = new Set((nextParticipants || []).map((p) => p.id));
  return (prevParticipants || []).map((p) => p.id).filter((id) => !nextIds.has(id));
}

function stripParticipantIdsFromBlocks(blocks, removedIds) {
  if (!removedIds.length) return blocks;
  const rm = new Set(removedIds);
  return (blocks || []).map((b) => {
    const sa =
      b.studentAssignments && typeof b.studentAssignments === 'object'
        ? { ...b.studentAssignments }
        : {};
    let touched = false;
    rm.forEach((id) => {
      if (Object.prototype.hasOwnProperty.call(sa, id)) {
        delete sa[id];
        touched = true;
      }
    });
    return touched ? { ...b, studentAssignments: sa } : b;
  });
}

async function deleteFuryLabAssignmentDocsForFlyers(eventId, participantIds) {
  if (!participantIds.length) return;
  await Promise.all(
    participantIds.map((pid) =>
      deleteDoc(doc(firestore, 'furyLabAssignments', assignmentDocId(eventId, pid))).catch(() => {})
    )
  );
}

function buildFuryLabTicketHoldersFromFlyers(flyersList) {
  return (flyersList || [])
    .filter(
      (f) =>
        f &&
        f.type === 'freshling' &&
        !f.cancelled &&
        f.manifestTickets &&
        f.manifestTickets[FURY_LAB_SUB_EVENT_LABEL] > 0
    )
    .map((f) => ({
      id: f.id,
      name: (getDisplayName(f) || f.name || '').trim(),
      email: (f.email || '').trim(),
      meetTime: '',
    }));
}
const WATER_THUR_LABEL = 'Water Training (Thur)';
const WATER_FRI_LABEL = 'Water Training (Fri)';
const CANOPY_THUR_LABEL = 'Canopy Thursday';
const CANOPY_FRI_LABEL = 'Canopy Friday';

/** Return sorted list of { start, coachName } for a participant's assigned blocks. */
function getParticipantAssignedSlots(
  participantId,
  assignments,
  blocks,
  timeSlots,
  coachesById
) {
  const a = assignments.find((x) => x.participantId === participantId);
  if (!a || !a.blockIds || !a.blockIds.length || !blocks || !timeSlots) return [];
  const blockMap = new Map(blocks.map((b) => [b.id, b]));
  const slotStarts = (timeSlots || []).map((s) => s.start);
  return a.blockIds
    .map((bid) => {
      const block = blockMap.get(bid);
      if (!block) return null;
      const start = slotStarts[block.slotIndex];
      let coachName = '';
      const blockCoaches = Array.isArray(block.coaches)
        ? block.coaches
        : block.coachId
        ? [block.coachId]
        : [];
      const studentAssignments =
        block.studentAssignments && typeof block.studentAssignments === 'object'
          ? block.studentAssignments
          : {};
      const assignedCoachId = studentAssignments[participantId] || blockCoaches[0] || '';
      if (assignedCoachId && coachesById) {
        const coach = coachesById.get(assignedCoachId);
        coachName = coach ? (coach.name || coach.email || '').trim() : '';
      }
      if (!coachName) {
        coachName = block.coachName || '';
      }
      return { start: start || '', coachName };
    })
    .filter(Boolean)
    .sort((x, y) => (x.start || '').localeCompare(y.start || ''));
}

/** Half-hour options for 30-minute blocks (06:00 through 23:30). */
const HALF_HOUR_OPTIONS = (() => {
  const options = [];
  for (let h = 6; h <= 23; h++) {
    options.push(`${h.toString().padStart(2, '0')}:00`);
    if (h < 23) options.push(`${h.toString().padStart(2, '0')}:30`);
  }
  return options;
})();

/** 15-minute options for meet time (06:00 through 23:45). */
const QUARTER_HOUR_OPTIONS = (() => {
  const options = [];
  for (let h = 6; h <= 23; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 23 && m > 45) break;
      options.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return options;
})();

function add30Minutes(timeStr) {
  if (!timeStr || !/^\d{1,2}:\d{2}$/.test(timeStr)) return '09:30';
  const [h, m] = timeStr.split(':').map((n) => parseInt(n, 10));
  let total = (h || 0) * 60 + (m || 0);
  total += 30;
  const outH = Math.floor(total / 60) % 24;
  const outM = total % 60;
  return `${outH.toString().padStart(2, '0')}:${outM.toString().padStart(2, '0')}`;
}

const FLYERS_OPTIONS = [1, 2, 3, 4, 5, 6, 8, 10, 12];
const defaultTimeSlots = () => [
  { start: '09:00', end: '09:30', flyers: 2 },
  { start: '09:30', end: '10:00', flyers: 2 },
];

function generateCoachId() {
  return `c-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function getBlockCoachIds(block) {
  if (!block) return [];
  const arr = Array.isArray(block.coaches) ? block.coaches : [];
  const filtered = arr.filter((id) => typeof id === 'string' && id);
  if (filtered.length) return filtered;
  return block.coachId ? [block.coachId] : [];
}

/** Display string for email / exports from ordered coach name list. */
function formatBlockCoachNameLabel(coachNames) {
  if (!coachNames || !coachNames.length) return '';
  if (coachNames.length === 1) return coachNames[0];
  if (coachNames.length === 2) return coachNames.join(' & ');
  return coachNames.join(', ');
}

/** Read-only display: prefer live flyer record, then stored participant row. */
function getParticipantDisplayName(p, flyer) {
  const fromFlyer = flyer ? (getDisplayName(flyer) || flyer.name || '').trim() : '';
  const stored = (p.name || '').trim();
  return fromFlyer || stored || '—';
}

function getParticipantDisplayEmail(p, flyer) {
  const fromFlyer = flyer ? String(flyer.email || '').trim() : '';
  const stored = (p.email || '').trim();
  return fromFlyer || stored || '—';
}

function buildParticipantReferenceSummary(flyer) {
  if (!flyer) return '';
  const bits = [];
  if (flyer.phone) bits.push(`Phone: ${flyer.phone}`);
  const ht = formatHeight(flyer.ht);
  const wt = formatWeight(flyer.wt);
  if (ht) bits.push(`Ht: ${ht}`);
  if (wt) bits.push(`Wt: ${wt}`);
  if (flyer.jumpNumber != null && flyer.jumpNumber !== '') bits.push(`Jumps: ${flyer.jumpNumber}`);
  if (flyer.tunnel) bits.push(`Tunnel: ${flyer.tunnel}`);
  return bits.length ? bits.join(' · ') : '';
}

function FuryLab() {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [config, setConfig] = useState({
    timeSlots: [],
    blocks: [],
    participants: [],
    coaches: [],
  });
  const [assignments, setAssignments] = useState([]);
  const [flyers, setFlyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const { emailComposerProps, openEmailComposer: pushEmailComposer, closeEmailComposer } =
    useEmailComposer();
  const [furyExport, setFuryExport] = useState(null); // { kind: 'schedule'|'participants', mode } | null
  const [furyExportCols, setFuryExportCols] = useState(() => new Set());
  const [participantDetailOpen, setParticipantDetailOpen] = useState({});
  const configRef = useRef(config);
  const assignmentsRef = useRef(assignments);
  const selectedEventIdRef = useRef(selectedEventId);

  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    assignmentsRef.current = assignments;
  }, [assignments]);
  useEffect(() => {
    selectedEventIdRef.current = selectedEventId;
  }, [selectedEventId]);

  useEffect(() => {
    let cancelled = false;
    async function fetchEvents() {
      try {
        const [snap, activeId] = await Promise.all([
          getDocs(collection(firestore, 'events')),
          getActiveEventId(),
        ]);
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setEvents(list);
        if (list.length) {
          setSelectedEventId((prev) =>
            prev && list.some((e) => e.id === prev)
              ? prev
              : resolveAdminDefaultEventId({ eventIdParam: '', activeEventId: activeId, events: list })
          );
        }
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load events');
      }
    }
    fetchEvents();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedEventId) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const [configSnap, assignSnap, flyersSnap] = await Promise.all([
          getDoc(doc(firestore, 'furyLabConfig', selectedEventId)),
          getDocs(
            query(
              collection(firestore, 'furyLabAssignments'),
              where('eventId', '==', selectedEventId)
            )
          ),
          getDocs(
            query(
              collection(firestore, 'flyers'),
              where('eventId', '==', selectedEventId)
            )
          ),
        ]);
        const assignmentList = assignSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const flyersList = flyersSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setFlyers(flyersList);

        const ticketHolders = buildFuryLabTicketHoldersFromFlyers(flyersList);

        const configDocRef = doc(firestore, 'furyLabConfig', selectedEventId);
        let loadedAssignments = assignmentList;
        if (configSnap.exists()) {
          const data = configSnap.data();
          const slots = (Array.isArray(data.timeSlots) ? data.timeSlots : []).map((s) => ({
            ...s,
            end: s.end || add30Minutes(s.start),
            flyers: Math.max(1, Math.min(12, Number(s.flyers) || 2)),
          }));
          const participants = (Array.isArray(data.participants) ? data.participants : []).map(
            (p) => ({ ...p, meetTime: p.meetTime != null ? p.meetTime : '' })
          );
          const coaches = Array.isArray(data.coaches) ? data.coaches : [];
          const normalizedBlocks = deriveBlocksFromTimeSlots(slots, data.blocks || []);
          const merged = mergeFuryLabParticipantsFromTickets(ticketHolders, participants);
          let finalParticipants = participants;
          let finalBlocks = normalizedBlocks;
          if (!furyLabParticipantsListsEqual(participants, merged)) {
            finalParticipants = merged;
            const removedIds = participantIdsRemovedFromRoster(participants, merged);
            finalBlocks =
              removedIds.length > 0
                ? stripParticipantIdsFromBlocks(normalizedBlocks, removedIds)
                : normalizedBlocks;
            if (removedIds.length > 0) {
              await deleteFuryLabAssignmentDocsForFlyers(selectedEventId, removedIds);
              loadedAssignments = assignmentList.filter((a) => !removedIds.includes(a.participantId));
            }
            await setDoc(configDocRef, {
              timeSlots: slots,
              blocks: finalBlocks,
              participants: merged,
              coaches,
            });
          }
          setConfig({
            timeSlots: slots,
            blocks: finalBlocks,
            participants: finalParticipants,
            coaches,
          });
        } else {
          const defaultConfig = {
            timeSlots: defaultTimeSlots(),
            blocks: deriveBlocksFromTimeSlots(defaultTimeSlots(), []),
            participants: ticketHolders,
            coaches: [],
          };
          await setDoc(configDocRef, defaultConfig);
          setConfig(defaultConfig);
        }

        setAssignments(loadedAssignments);
      } catch (e) {
        setError(e.message || 'Failed to load Fury Lab data');
      } finally {
        setLoading(false);
      }
    })();
  }, [selectedEventId]);

  /** Live-sync participant list when flyers / manifest tickets change (debounced). */
  useEffect(() => {
    if (!selectedEventId || loading) return undefined;

    const eventId = selectedEventId;
    const flyersQuery = query(
      collection(firestore, 'flyers'),
      where('eventId', '==', selectedEventId)
    );

    let debounceTimer = null;

    const unsubscribe = onSnapshot(
      flyersQuery,
      (snap) => {
        const flyersList = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        clearTimeout(debounceTimer);
        debounceTimer = window.setTimeout(async () => {
          if (eventId !== selectedEventIdRef.current) return;
          try {
            setFlyers(flyersList);
            const holders = buildFuryLabTicketHoldersFromFlyers(flyersList);
            const prev = configRef.current;
            const merged = mergeFuryLabParticipantsFromTickets(holders, prev.participants);
            if (furyLabParticipantsListsEqual(prev.participants, merged)) return;
            const removedIds = participantIdsRemovedFromRoster(prev.participants, merged);
            const nextBlocks =
              removedIds.length > 0
                ? stripParticipantIdsFromBlocks(prev.blocks, removedIds)
                : prev.blocks;
            if (removedIds.length > 0) {
              await deleteFuryLabAssignmentDocsForFlyers(eventId, removedIds);
            }
            const next = {
              ...prev,
              participants: merged,
              blocks: nextBlocks,
            };
            await setDoc(doc(firestore, 'furyLabConfig', eventId), {
              timeSlots: next.timeSlots,
              blocks: nextBlocks,
              participants: merged,
              coaches: next.coaches,
            });
            if (eventId !== selectedEventIdRef.current) return;
            setConfig(next);
            if (removedIds.length > 0) {
              setAssignments((a) => a.filter((x) => !removedIds.includes(x.participantId)));
            }
          } catch (e) {
            if (eventId === selectedEventIdRef.current) {
              setError(e.message || 'Failed to sync participants from tickets');
            }
          }
        }, 400);
      },
      (e) => {
        if (eventId === selectedEventIdRef.current) {
          setError(e.message || 'Flyer subscription error');
        }
      }
    );

    return () => {
      clearTimeout(debounceTimer);
      unsubscribe();
    };
  }, [selectedEventId, loading]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) || null,
    [events, selectedEventId]
  );
  const eventDateLabel = useMemo(() => {
    if (!selectedEvent) return '—';
    const lab = getFuryLabDateLabel(selectedEvent);
    if (lab) return lab;
    return selectedEvent.date ? formatEventDate(selectedEvent.date) : '—';
  }, [selectedEvent]);

  const blocks = useMemo(
    () =>
      config.blocks.length > 0
        ? config.blocks
        : deriveBlocksFromTimeSlots(config.timeSlots, []),
    [config.blocks, config.timeSlots]
  );

  const saveConfig = async (nextConfig) => {
    if (!selectedEventId) return;
    const nextBlocks =
      nextConfig.blocks && nextConfig.blocks.length > 0
        ? nextConfig.blocks
        : deriveBlocksFromTimeSlots(nextConfig.timeSlots || config.timeSlots, nextConfig.blocks || config.blocks);
    const payload = {
      timeSlots: nextConfig.timeSlots ?? config.timeSlots,
      blocks: nextBlocks,
      participants: nextConfig.participants ?? config.participants,
      coaches: nextConfig.coaches ?? config.coaches,
    };
    setConfig(payload);
    setSaving(true);
    setError('');
    try {
      await setDoc(doc(firestore, 'furyLabConfig', selectedEventId), payload);
    } catch (e) {
      setError(e.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTimeSlotsChange = (nextSlots) => {
    const nextBlocks = deriveBlocksFromTimeSlots(nextSlots, config.blocks);
    saveConfig({ ...config, timeSlots: nextSlots, blocks: nextBlocks });
  };

  const addTimeSlot = () => {
    const next = [...config.timeSlots, { start: '09:00', end: '09:30', flyers: 2 }];
    handleTimeSlotsChange(next);
  };

  const removeTimeSlot = (index) => {
    const next = config.timeSlots.filter((_, i) => i !== index);
    handleTimeSlotsChange(next);
  };

  const updateTimeSlot = (index, field, value) => {
    const next = config.timeSlots.map((s, i) => {
      if (i !== index) return s;
      const updated = { ...s, [field]: value };
      if (field === 'start') updated.end = add30Minutes(value);
      if (field === 'flyers') updated.flyers = Math.max(1, Math.min(12, parseInt(value, 10) || 2));
      return updated;
    });
    handleTimeSlotsChange(next);
  };

  const coachesById = useMemo(() => {
    const map = new Map();
    (config.coaches || []).forEach((c) => map.set(c.id, c));
    return map;
  }, [config.coaches]);

  const applyBlockCoachIds = (blockIdVal, coachIdsInput) => {
    const raw = (coachIdsInput || []).filter((id) => typeof id === 'string' && id);
    const unique = [];
    raw.forEach((id) => {
      if (!unique.includes(id)) unique.push(id);
    });
    const coaches = unique;

    const nextBlocks = config.blocks.map((b) => {
      if (b.id !== blockIdVal) return b;
      const prevAssignments =
        b.studentAssignments && typeof b.studentAssignments === 'object'
          ? b.studentAssignments
          : {};
      const assignedIdsForBlock = (participantIdsByBlockId.get(b.id) || []).filter(
        (pid) => typeof pid === 'string' && pid
      );

      let nextStudentAssignments = { ...prevAssignments };
      if (coaches.length === 0) {
        nextStudentAssignments = {};
      } else if (coaches.length === 1) {
        const onlyCoachId = coaches[0];
        assignedIdsForBlock.forEach((pid) => {
          nextStudentAssignments[pid] = onlyCoachId;
        });
      } else {
        const coachSet = new Set(coaches);
        assignedIdsForBlock.forEach((pid) => {
          const current = prevAssignments[pid];
          if (current && coachSet.has(current)) {
            nextStudentAssignments[pid] = current;
          } else {
            nextStudentAssignments[pid] = coaches[0];
          }
        });
      }

      const coachNames = coaches
        .map((id) => {
          const coach = coachesById.get(id);
          return coach ? (coach.name || coach.email || '').trim() : '';
        })
        .filter(Boolean);
      const coachNameLabel = formatBlockCoachNameLabel(coachNames);

      return {
        ...b,
        coachId: coaches[0] || '',
        coachName: coachNameLabel,
        coaches,
        studentAssignments: nextStudentAssignments,
      };
    });
    saveConfig({ ...config, blocks: nextBlocks });
  };

  const addCoachToSlotBlock = (blockIdVal, coachId) => {
    if (!coachId) return;
    const b = config.blocks.find((block) => block.id === blockIdVal);
    if (!b) return;
    const current = getBlockCoachIds(b);
    if (current.includes(coachId)) return;
    applyBlockCoachIds(blockIdVal, [...current, coachId]);
  };

  const removeCoachFromSlotBlock = (blockIdVal, coachId) => {
    const b = config.blocks.find((block) => block.id === blockIdVal);
    if (!b) return;
    const current = getBlockCoachIds(b);
    applyBlockCoachIds(blockIdVal, current.filter((id) => id !== coachId));
  };

  const updateStudentCoachForBlock = (blockIdVal, participantId, coachId) => {
    if (!coachId) return;
    const nextBlocks = config.blocks.map((b) => {
      if (b.id !== blockIdVal) return b;
      const blockCoaches = Array.isArray(b.coaches)
        ? b.coaches
        : b.coachId
        ? [b.coachId]
        : [];
      if (!blockCoaches.includes(coachId)) return b;
      const prevAssignments =
        b.studentAssignments && typeof b.studentAssignments === 'object'
          ? b.studentAssignments
          : {};
      return {
        ...b,
        studentAssignments: {
          ...prevAssignments,
          [participantId]: coachId,
        },
      };
    });
    saveConfig({ ...config, blocks: nextBlocks });
  };

  const addCoach = () => {
    const next = [...(config.coaches || []), { id: generateCoachId(), name: '', email: '' }];
    saveConfig({ ...config, coaches: next });
  };

  const updateCoach = (id, field, value) => {
    const next = (config.coaches || []).map((c) =>
      c.id === id ? { ...c, [field]: value } : c
    );
    saveConfig({ ...config, coaches: next });
  };

  const removeCoach = (id) => {
    const nextCoaches = (config.coaches || []).filter((c) => c.id !== id);
    const nextBlocks = config.blocks.map((b) => {
      const blockCoaches = Array.isArray(b.coaches)
        ? b.coaches
        : b.coachId
        ? [b.coachId]
        : [];
      if (!blockCoaches.includes(id)) return b;
      const remaining = blockCoaches.filter((cid) => cid !== id);
      const prevAssignments =
        b.studentAssignments && typeof b.studentAssignments === 'object'
          ? b.studentAssignments
          : {};
      let nextStudentAssignments = { ...prevAssignments };
      if (remaining.length === 0) {
        nextStudentAssignments = {};
      } else {
        const fallbackCoachId = remaining[0];
        Object.keys(prevAssignments).forEach((pid) => {
          if (prevAssignments[pid] === id) {
            nextStudentAssignments[pid] = fallbackCoachId;
          }
        });
      }
      const coachNames = remaining
        .map((cid) => {
          const coach = coachesById.get(cid);
          return coach ? (coach.name || coach.email || '').trim() : '';
        })
        .filter(Boolean);
      const coachNameLabel = formatBlockCoachNameLabel(coachNames);
      return {
        ...b,
        coachId: remaining[0] || '',
        coachName: coachNameLabel,
        coaches: remaining,
        studentAssignments: nextStudentAssignments,
      };
    });
    saveConfig({ ...config, coaches: nextCoaches, blocks: nextBlocks });
  };

  const updateParticipantMeetTime = (id, meetTime) => {
    const next = config.participants.map((p) =>
      p.id === id ? { ...p, meetTime } : p
    );
    saveConfig({ ...config, participants: next });
  };

  const assignmentByParticipant = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => map.set(a.participantId, a));
    return map;
  }, [assignments]);

  const furyLabTicketHolders = useMemo(
    () => buildFuryLabTicketHoldersFromFlyers(flyers),
    [flyers]
  );

  // Alert when someone has conflicting Thursday/Friday bookings or Fury Lab + Canopy Thursday
  useEffect(() => {
    if (!selectedEventId || loading || !flyers.length || !config.participants.length) return;
    const conflicts = [];
    config.participants.forEach((p) => {
      const flyer = flyers.find((f) => f.id === p.id);
      if (!flyer?.manifestTickets) return;
      const t = flyer.manifestTickets;
      const name = (p.name || getDisplayName(flyer) || flyer.email || p.id).trim() || 'Unknown';
      const hasWaterThur = !!(t[WATER_THUR_LABEL] > 0);
      const hasCanopyThur = !!(t[CANOPY_THUR_LABEL] > 0);
      const hasWaterFri = !!(t[WATER_FRI_LABEL] > 0);
      const hasCanopyFri = !!(t[CANOPY_FRI_LABEL] > 0);
      if (hasCanopyThur && hasWaterThur) conflicts.push(`${name}: Canopy Thursday and Water Thursday`);
      if (hasCanopyFri && hasWaterFri) conflicts.push(`${name}: Canopy Friday and Water Friday`);
      if (hasCanopyThur) conflicts.push(`${name}: Fury Lab and Canopy Thursday`);
    });
    if (conflicts.length > 0) {
      window.alert(
        'Schedule conflict(s):\n\n' + conflicts.join('\n')
      );
    }
  }, [selectedEventId, loading, flyers, config.participants]);

  const participantIdsByBlockId = useMemo(() => {
    const map = new Map();
    assignments.forEach((a) => {
      (a.blockIds || []).forEach((bid) => {
        if (!map.has(bid)) map.set(bid, []);
        map.get(bid).push(a.participantId);
      });
    });
    return map;
  }, [assignments]);

  const getEarliestMeetTimeForSlot = useCallback(
    (slotIndex) => {
      const b = blocks.find((block) => block.slotIndex === slotIndex);
      if (!b) return '—';
      const assignedIds = participantIdsByBlockId.get(b.id) || [];
      const meetTimes = assignedIds
        .map((pid) => {
          const p = config.participants.find((x) => x.id === pid);
          return (p && p.meetTime) || '';
        })
        .filter(Boolean)
        .sort();
      return meetTimes.length > 0 ? meetTimes[0] : '—';
    },
    [blocks, participantIdsByBlockId, config.participants]
  );

  const furyScheduleExportRows = useMemo(() => {
    if (!config.timeSlots.length) return [];
    const slotsWithIndex = config.timeSlots.map((slot, slotIndex) => ({ slot, slotIndex }));
    return slotsWithIndex
      .slice()
      .sort((a, b) => (a.slot.start || '').localeCompare(b.slot.start || ''))
      .map(({ slot, slotIndex }) => {
        const b = blocks.find((block) => block.slotIndex === slotIndex);
        const assignedIds = b ? participantIdsByBlockId.get(b.id) || [] : [];
        const coachName = b && b.coachName ? b.coachName : '—';
        const flyingNames = assignedIds
          .map((pid) => {
            const p = config.participants.find((x) => x.id === pid);
            return p ? (p.name || p.email || '').trim() || pid : pid;
          })
          .filter(Boolean);
        const meetTime = getEarliestMeetTimeForSlot(slotIndex);
        const flyingLabel = flyingNames.length > 0 ? flyingNames.join(', ') : '—';
        return {
          timeSlot: slot.start || '',
          meetTime,
          coach: coachName,
          flying: flyingLabel,
        };
      });
  }, [config.timeSlots, config.participants, blocks, participantIdsByBlockId, getEarliestMeetTimeForSlot]);

  const furyParticipantExportRows = useMemo(() => {
    return config.participants.map((p) => {
      const assignedSlots = getParticipantAssignedSlots(
        p.id,
        assignments,
        blocks,
        config.timeSlots,
        coachesById
      );
      const assignedSummary =
        assignedSlots.length > 0
          ? assignedSlots.map((s) => s.start).join(', ')
          : '—';
      const coachesSummary =
        assignedSlots.length > 0
          ? [...new Set(assignedSlots.map((s) => s.coachName).filter(Boolean))].join(', ') || '—'
          : '—';
      const flyer = flyers.find((f) => f.id === p.id);
      const hasWaterThur = !!(flyer?.manifestTickets && flyer.manifestTickets[WATER_THUR_LABEL] > 0);
      const hasCanopyThur = !!(flyer?.manifestTickets && flyer.manifestTickets[CANOPY_THUR_LABEL] > 0);
      const thuTraining =
        hasWaterThur && hasCanopyThur
          ? 'Water, Canopy'
          : hasWaterThur
          ? 'Water'
          : hasCanopyThur
          ? 'Canopy'
          : '—';
      const name = (p.name || '').trim() || (flyer && (flyer.name || '')) || '';
      const email = (p.email || '').trim() || (flyer && (flyer.email || '')) || '';
      const ass = assignmentByParticipant.get(p.id);
      const emailStatus = [
        ass?.initialEmailSentAt ? 'Initial' : '',
        ass?.reminderEmailSentAt ? 'Reminder' : '',
      ]
        .filter(Boolean)
        .join(' | ') || '—';
      return {
        name: name || '—',
        email,
        phone: flyer?.phone || '—',
        meetTime: p.meetTime || '—',
        assignedTimes: assignedSummary,
        coaches: coachesSummary,
        waterCanopyThu: thuTraining,
        ht: flyer ? formatHeight(flyer.ht) || '—' : '—',
        wt: flyer ? formatWeight(flyer.wt) || '—' : '—',
        jumps:
          flyer && flyer.jumpNumber != null && flyer.jumpNumber !== ''
            ? String(flyer.jumpNumber)
            : '—',
        tunnel: flyer ? flyer.tunnel || '—' : '—',
        emailStatus,
      };
    });
  }, [
    config.participants,
    assignments,
    blocks,
    config.timeSlots,
    coachesById,
    flyers,
    assignmentByParticipant,
  ]);

  const getFuryExportColumns = (kind) =>
    kind === 'schedule' ? FURY_SCHEDULE_EXPORT_COLS : FURY_PARTICIPANT_EXPORT_COLS;

  useEffect(() => {
    if (!furyExport) return;
    const cols = getFuryExportColumns(furyExport.kind);
    setFuryExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  }, [furyExport]);

  const openFuryExport = (kind, mode) => {
    const rows = kind === 'schedule' ? furyScheduleExportRows : furyParticipantExportRows;
    if (!rows.length) {
      window.alert(
        kind === 'schedule'
          ? 'No time slots configured to export.'
          : 'No participants to export.'
      );
      return;
    }
    setFuryExport({ kind, mode });
  };

  const resetFuryExportCols = () => {
    if (!furyExport) return;
    const cols = getFuryExportColumns(furyExport.kind);
    setFuryExportCols(new Set(cols.filter((c) => c.default).map((c) => c.id)));
  };

  const runFuryExport = (selectedColumnIds) => {
    if (!furyExport) return;
    const { kind, mode } = furyExport;
    const allCols = getFuryExportColumns(kind);
    const colSet =
      Array.isArray(selectedColumnIds) && selectedColumnIds.length > 0
        ? new Set(selectedColumnIds)
        : furyExportCols;
    const active = allCols.filter((c) => colSet.has(c.id));
    if (!active.length) return;
    const baseRows = kind === 'schedule' ? furyScheduleExportRows : furyParticipantExportRows;
    const colMeta = active.map((c) => ({ id: c.id, header: c.label }));
    const rowRecords = baseRows.map((r) => {
      const o = {};
      active.forEach((c) => {
        o[c.id] = r[c.id] ?? '';
      });
      return o;
    });
    const scheduleTitle =
      selectedEvent && eventDateLabel && eventDateLabel !== '—'
        ? `Fury Lab – ${eventDateLabel}`
        : 'Fury Lab';
    const sub = kind === 'schedule' ? 'Schedule by time slot' : 'Participants';
    const titleBase = kind === 'schedule' ? scheduleTitle : `${scheduleTitle} — Participants`;
    if (mode === 'copy') {
      copyText(buildTsvTable(colMeta, rowRecords));
    } else {
      const headerCells = active.map((c) => `<th>${escapeHtmlCell(c.label)}</th>`).join('');
      const rowsHtml = rowRecords
        .map((rec) => {
          const cells = active.map((c) => `<td>${escapeHtmlCell(rec[c.id])}</td>`).join('');
          return `<tr>${cells}</tr>`;
        })
        .join('');
      const bodyHtml = `<main style="padding:8px 12px"><h1>${escapeHtmlCell(titleBase)}</h1><p>${escapeHtmlCell(sub)}</p><p>${rowRecords.length} row(s)</p><table><thead><tr>${headerCells}</tr></thead><tbody>${rowsHtml}</tbody></table></main>`;
      openPrintWindow(`${titleBase} — ${sub}`, bodyHtml, getOperationalTablePrintCss());
    }
    setFuryExport(null);
  };

  const assignParticipantToBlock = async (participantId, blockIdVal) => {
    const b = config.blocks.find((block) => block.id === blockIdVal);
    const slot = b != null && config.timeSlots[b.slotIndex] ? config.timeSlots[b.slotIndex] : null;
    const maxFlyers = slot ? (Math.max(1, Math.min(12, Number(slot.flyers) || 2))) : 2;
    const currentCount = (participantIdsByBlockId.get(blockIdVal) || []).length;
    if (currentCount >= maxFlyers) return;
    const existing = assignmentByParticipant.get(participantId);
    const currentIds = existing && Array.isArray(existing.blockIds) ? existing.blockIds : [];
    if (currentIds.includes(blockIdVal)) return;
    const nextIds = [...currentIds, blockIdVal];
    const aid = assignmentDocId(selectedEventId, participantId);
    const ref = doc(firestore, 'furyLabAssignments', aid);
    try {
      if (existing) {
        await updateDoc(ref, { blockIds: nextIds });
      } else {
        await setDoc(ref, {
          eventId: selectedEventId,
          participantId,
          blockIds: nextIds,
        });
      }
      setAssignments((prev) =>
        prev.some((a) => a.participantId === participantId)
          ? prev.map((a) =>
              a.participantId === participantId
                ? { ...a, blockIds: nextIds }
                : a
            )
          : [
              ...prev,
              {
                id: aid,
                eventId: selectedEventId,
                participantId,
                blockIds: nextIds,
              },
            ]
      );
      if (b) {
        const blockCoaches = Array.isArray(b.coaches)
          ? b.coaches
          : b.coachId
          ? [b.coachId]
          : [];
        const defaultCoachId = blockCoaches[0] || '';
        if (defaultCoachId) {
          const nextBlocks = config.blocks.map((block) => {
            if (block.id !== b.id) return block;
            const prevAssignments =
              block.studentAssignments && typeof block.studentAssignments === 'object'
                ? block.studentAssignments
                : {};
            if (prevAssignments[participantId]) return block;
            return {
              ...block,
              studentAssignments: {
                ...prevAssignments,
                [participantId]: defaultCoachId,
              },
            };
          });
          saveConfig({ ...config, blocks: nextBlocks });
        }
      }
    } catch (e) {
      setError(e.message || 'Failed to assign');
    }
  };

  const unassignParticipantFromBlock = async (participantId, blockIdVal) => {
    const aid = assignmentDocId(selectedEventId, participantId);
    const ref = doc(firestore, 'furyLabAssignments', aid);
    const existing = assignmentByParticipant.get(participantId);
    const currentIds = (existing && existing.blockIds) || [];
    const nextIds = currentIds.filter((id) => id !== blockIdVal);
    try {
      if (nextIds.length === 0) {
        await updateDoc(ref, { blockIds: [] });
      } else {
        await updateDoc(ref, { blockIds: nextIds });
      }
      setAssignments((prev) =>
        prev.map((a) =>
          a.participantId === participantId ? { ...a, blockIds: nextIds } : a
        )
      );
      const nextBlocks = config.blocks.map((block) => {
        if (block.id !== blockIdVal) return block;
        const prevAssignments =
          block.studentAssignments && typeof block.studentAssignments === 'object'
            ? block.studentAssignments
            : {};
        if (!(participantId in prevAssignments)) return block;
        const { [participantId]: _removed, ...rest } = prevAssignments;
        return {
          ...block,
          studentAssignments: rest,
        };
      });
      saveConfig({ ...config, blocks: nextBlocks });
    } catch (e) {
      setError(e.message || 'Failed to unassign');
    }
  };

  const openEmailComposer = (emailType, filterNotSent = false) => {
    const assignedParticipantIds = [
      ...new Set(assignments.map((a) => a.participantId).filter(Boolean)),
    ];
    const participantsWithAssignments = config.participants.filter((p) =>
      assignedParticipantIds.includes(p.id)
    );
    let toSend = participantsWithAssignments;
    if (filterNotSent) {
      const field = emailType === 'initial' ? 'initialEmailSentAt' : 'reminderEmailSentAt';
      toSend = participantsWithAssignments.filter((p) => {
        const a = assignmentByParticipant.get(p.id);
        return !(a && a[field]);
      });
    }
    // Personalized send/preview should be one email per participant, not per coach.
    // Only include Fury Lab participants (freshlings) here; coaches can be emailed separately if needed.
    const participantEmails = toSend.map((p) => (p.email || '').trim()).filter(Boolean);
    const allBcc = [...new Set(participantEmails)];
    if (!allBcc.length) {
      window.alert('No participants with email addresses to send to.');
      return;
    }
    // Enrich each participant with blockSummary and coachName for mail-merge (same system as recurrency).
    const registrants = toSend.map((p) => {
      const a = assignmentByParticipant.get(p.id);
      const blockSummaryStr =
        a && a.blockIds && a.blockIds.length
          ? buildBlockSummary(a.blockIds, config.blocks, config.timeSlots)
          : '';
      const firstBlockId = a && a.blockIds && a.blockIds.length ? a.blockIds[0] : null;
      const firstBlock = firstBlockId
        ? (config.blocks || []).find((b) => b.id === firstBlockId)
        : null;
      const coachNameStr = (firstBlock && firstBlock.coachName) ? firstBlock.coachName : '';
      return { ...p, blockSummary: blockSummaryStr, coachName: coachNameStr };
    });

    pushEmailComposer({
      context: {
        furyLab: true,
        emailType,
        eventId: selectedEventId,
        participantIds: toSend.map((p) => p.id),
        event: selectedEvent || undefined,
        eventDateLabel: eventDateLabel || '',
        registrants,
      },
      to: '',
      bcc: allBcc.join(', '),
      template: emailType === 'initial' ? 'fury_lab_initial' : 'fury_lab_reminder',
    });
  };

  const toggleParticipantDetail = (id) => {
    setParticipantDetailOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEmailSent = async ({ context: emailContext }) => {
    if (!emailContext || !emailContext.furyLab || !emailContext.participantIds) return;
    const field =
      emailContext.emailType === 'initial' ? 'initialEmailSentAt' : 'reminderEmailSentAt';
    try {
      for (const participantId of emailContext.participantIds) {
        const aid = assignmentDocId(selectedEventId, participantId);
        const ref = doc(firestore, 'furyLabAssignments', aid);
        await updateDoc(ref, { [field]: serverTimestamp() });
      }
      setAssignments((prev) =>
        prev.map((a) =>
          emailContext.participantIds.includes(a.participantId)
            ? { ...a, [field]: new Date() }
            : a
        )
      );
    } catch (e) {
      setError(e.message || 'Failed to update email status.');
    }
  };

  if (loading && !config.timeSlots.length && selectedEventId) {
    return (
      <div className="fury-lab">
        <p>Loading Fury Lab…</p>
      </div>
    );
  }

  return (
    <div className="fury-lab">
      <header className="fury-lab-header">
        <h2>Fury Lab</h2>
      </header>

      {error && <p className="fury-lab-error">{error}</p>}

      {!selectedEventId ? (
        <div className="fury-lab-empty-state">
          <p>Select an event to configure Fury Lab.</p>
          <label className="fury-lab-event-picker">
            Event{' '}
            <select
              value={selectedEventId}
              onChange={(e) => setSelectedEventId(e.target.value)}
            >
              <option value="">Select event</option>
              {events.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.title || e.id} {e.date ? `(${formatEventDate(e.date)})` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
      ) : (
        <>
          <div className="fury-lab-workspace">
            <aside className="fury-lab-setup-panel" aria-label="Fury Lab setup">
              <label className="fury-lab-setup-event">
                Event{' '}
                <select
                  value={selectedEventId}
                  onChange={(e) => setSelectedEventId(e.target.value)}
                >
                  {events.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title || e.id} {e.date ? `(${formatEventDate(e.date)})` : ''}
                    </option>
                  ))}
                </select>
              </label>
              {eventDateLabel && (
                <p className="fury-lab-setup-date">Fury Lab day: {eventDateLabel}</p>
              )}
              <div className="fury-lab-setup-section">
                <h3>Coaches</h3>
                <p className="fury-lab-hint fury-lab-hint--compact">
                  Name and email for the grid and BCC on participant emails when you use the composer.
                </p>
                <div className="fury-lab-coach-list">
                  {(config.coaches || []).map((c) => (
                    <div key={c.id} className="fury-lab-coach-row fury-lab-coach-row--compact">
                      <input
                        type="text"
                        placeholder="Name"
                        value={c.name}
                        onChange={(e) => updateCoach(c.id, 'name', e.target.value)}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={c.email}
                        onChange={(e) => updateCoach(c.id, 'email', e.target.value)}
                      />
                      <button
                        type="button"
                        className="fury-lab-btn-danger-text"
                        onClick={() => removeCoach(c.id)}
                        aria-label="Remove coach"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="fury-lab-btn-ghost fury-lab-btn-ghost--small"
                    onClick={addCoach}
                  >
                    Add coach
                  </button>
                </div>
              </div>
              <button
                type="button"
                className="btn-fm-primary fury-lab-btn-primary--block"
                onClick={addTimeSlot}
              >
                Add time slot
              </button>
            </aside>

            <section className="fury-lab-schedule-panel" aria-label="Schedule grid">
              <p className="fury-lab-hint fury-lab-hint--grid">
                Each row is one 30-minute slot. Add one or more coaches as pills, then assign flyers. Slots start on the hour or half-hour.
              </p>
              <div className="fury-lab-schedule-grid">
                <div className="fury-lab-schedule-row fury-lab-schedule-row--header">
                  <span>Start</span>
                  <span>Cap</span>
                  <span>Coaches</span>
                  <span>Flyers</span>
                  <span className="fury-lab-schedule-slot-actions-h" aria-hidden="true" />
                </div>
                {config.timeSlots.map((slot, slotIndex) => {
                  const b = blocks.find((block) => block.slotIndex === slotIndex);
                  if (!b) return null;
                  const assignedIds = participantIdsByBlockId.get(b.id) || [];
                  const maxFlyers = Math.max(1, Math.min(12, Number(slot.flyers) || 2));
                  const atCap = assignedIds.length >= maxFlyers;
                  const blockCoaches = getBlockCoachIds(b);
                  const coachesAvailable = config.coaches || [];
                  const unassignedCoaches = coachesAvailable.filter((c) => !blockCoaches.includes(c.id));
                  return (
                    <div key={slotIndex} className="fury-lab-schedule-row">
                      <div className="fury-lab-schedule-cell fury-lab-schedule-cell--start">
                        <select
                          value={slot.start}
                          onChange={(e) => updateTimeSlot(slotIndex, 'start', e.target.value)}
                          className="fury-lab-time-select"
                          aria-label={`Slot ${slotIndex + 1} start time`}
                        >
                          {HALF_HOUR_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="fury-lab-schedule-cell fury-lab-schedule-cell--cap">
                        <select
                          value={slot.flyers ?? 2}
                          onChange={(e) => updateTimeSlot(slotIndex, 'flyers', e.target.value)}
                          className="fury-lab-flyers-select"
                          aria-label={`Slot ${slotIndex + 1} flyer capacity`}
                        >
                          {FLYERS_OPTIONS.map((n) => (
                            <option key={n} value={n}>
                              {n}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="fury-lab-schedule-cell fury-lab-schedule-cell--coaches">
                        <div className="fury-lab-block-coaches">
                          {blockCoaches.map((cid) => {
                            const coach = coachesById.get(cid);
                            const coachLabel = coach ? coach.name || coach.email || cid : cid;
                            return (
                              <span key={cid} className="fury-lab-coach-pill">
                                <span className="fury-lab-coach-pill-text">{coachLabel}</span>
                                <button
                                  type="button"
                                  className="fury-lab-coach-pill-remove"
                                  onClick={() => removeCoachFromSlotBlock(b.id, cid)}
                                  aria-label={`Remove ${coachLabel} from slot`}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                          {coachesAvailable.length === 0 ? (
                            <span className="fury-lab-coaches-empty-hint">
                              Add coaches in the left panel
                            </span>
                          ) : (
                            unassignedCoaches.length > 0 && (
                              <select
                                className="fury-lab-add-coach"
                                value=""
                                onChange={(e) => {
                                  const id = e.target.value;
                                  if (id) {
                                    addCoachToSlotBlock(b.id, id);
                                    e.target.value = '';
                                  }
                                }}
                                aria-label={`Add coach to ${slot.start}`}
                              >
                                <option value="">Add coach…</option>
                                {unassignedCoaches.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name || c.email || c.id}
                                  </option>
                                ))}
                              </select>
                            )
                          )}
                        </div>
                      </div>
                      <div className="fury-lab-schedule-cell fury-lab-schedule-cell--flyers">
                        <span className="fury-lab-slot-cap" aria-hidden="true">
                          {assignedIds.length}/{maxFlyers}
                        </span>
                        <div className="fury-lab-block-assignments">
                          {assignedIds.map((pid) => {
                            const participant = config.participants.find((x) => x.id === pid);
                            const label = participant
                              ? participant.name || participant.email || pid
                              : pid;
                            const blockAssignments =
                              b.studentAssignments && typeof b.studentAssignments === 'object'
                                ? b.studentAssignments
                                : {};
                            const assignedCoachIdForParticipant =
                              blockAssignments[pid] || blockCoaches[0] || '';
                            return (
                              <span key={pid} className="fury-lab-assigned-pill">
                                {blockCoaches.length > 1 && (
                                  <select
                                    className="fury-lab-assigned-coach-select"
                                    value={assignedCoachIdForParticipant}
                                    onChange={(e) =>
                                      updateStudentCoachForBlock(b.id, pid, e.target.value)
                                    }
                                    aria-label={`Coach for ${label}`}
                                  >
                                    {blockCoaches.map((cid) => {
                                      const coach = coachesById.get(cid);
                                      const coachLabel =
                                        coach ? coach.name || coach.email || coach.id : cid;
                                      return (
                                        <option key={cid} value={cid}>
                                          {coachLabel}
                                        </option>
                                      );
                                    })}
                                  </select>
                                )}
                                <span className="fury-lab-assigned-pill-text">{label}</span>
                                <button
                                  type="button"
                                  className="fury-lab-unassign"
                                  onClick={() => unassignParticipantFromBlock(pid, b.id)}
                                  aria-label={`Remove ${label} from slot`}
                                >
                                  ×
                                </button>
                              </span>
                            );
                          })}
                          {!atCap && (
                            <select
                              className="fury-lab-add-person"
                              value=""
                              onChange={(e) => {
                                const pid = e.target.value;
                                if (pid) {
                                  assignParticipantToBlock(pid, b.id);
                                  e.target.value = '';
                                }
                              }}
                              aria-label={`Add flyer to ${slot.start}`}
                            >
                              <option value="">Add flyer…</option>
                              {config.participants
                                .filter((p) => !assignedIds.includes(p.id))
                                .map((p) => (
                                  <option key={p.id} value={p.id}>
                                    {p.name || p.email || p.id}
                                  </option>
                                ))}
                            </select>
                          )}
                        </div>
                      </div>
                      <div className="fury-lab-schedule-cell fury-lab-schedule-cell--actions">
                        <button
                          type="button"
                          className="fury-lab-btn-danger-text"
                          onClick={() => removeTimeSlot(slotIndex)}
                          aria-label={`Remove time slot ${slot.start}`}
                        >
                          Remove slot
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <section className="fury-lab-section fury-lab-participants-section">
            <h3>Participants</h3>
            <p className="fury-lab-hint">
              The list always matches who has a Fury Lab ticket on their flyer (updates automatically). Name and email are read-only here (change them in Flyer Management). Meet time is editable per row. If someone loses their ticket, they drop off this list and their slot assignments are cleared. Use the toolbar for email (BCC assigned participants), print/copy this table, or print/copy the schedule by time slot; email status shows in the Emails column.
            </p>
            <div className="fury-lab-participants-toolbar">
              <button
                type="button"
                className="fury-lab-btn-ghost"
                onClick={() => openEmailComposer('initial', false)}
                title="BCC everyone who has slot assignments and an email address."
              >
                Send initial email
              </button>
              <button
                type="button"
                className="fury-lab-btn-ghost"
                onClick={() => openEmailComposer('reminder', false)}
                title="BCC everyone who has slot assignments and an email address."
              >
                Send reminder email
              </button>
              <button
                type="button"
                className="fury-lab-btn-ghost btn-fm-print-outline"
                onClick={() => openFuryExport('participants', 'print')}
                disabled={!furyParticipantExportRows.length}
              >
                Print table
              </button>
              <AdminCopyMenu
                className="fury-lab-copy-menu"
                ariaLabel="Copy Fury Lab participants table"
                disabled={!furyParticipantExportRows.length}
                items={[
                  {
                    label: 'Copy table…',
                    onSelect: () => openFuryExport('participants', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
              <span className="fury-lab-participants-toolbar-sep" aria-hidden="true" />
              <button
                type="button"
                className="fury-lab-btn-ghost"
                onClick={() => openFuryExport('schedule', 'print')}
                disabled={!furyScheduleExportRows.length}
                title="Print-friendly view of the schedule grid (time slot, meet time, coaches, flyers)."
              >
                Print schedule
              </button>
              <AdminCopyMenu
                className="fury-lab-copy-menu"
                ariaLabel="Copy Fury Lab schedule table"
                disabled={!furyScheduleExportRows.length}
                items={[
                  {
                    label: 'Copy schedule…',
                    onSelect: () => openFuryExport('schedule', 'copy'),
                    showCopiedFeedback: false,
                  },
                ]}
              />
              {furyLabTicketHolders.length > 0 && (
                <span className="fury-lab-ticket-count">
                  {furyLabTicketHolders.length} with Fury Lab tickets
                </span>
              )}
            </div>
            <div className="fury-lab-participants">
              {config.participants.length > 0 && (
                <div className="fury-lab-participant-row fury-lab-participant-row--main fury-lab-participant-header">
                  <span className="fury-lab-participant-h-name">Name</span>
                  <span>Email</span>
                  <span>Meet</span>
                  <span>Slots</span>
                  <span>Coach</span>
                  <span className="fury-lab-thu-training-header">W/C Thu</span>
                  <span>Emails</span>
                </div>
              )}
              {config.participants.map((p) => {
                const assignedSlots = getParticipantAssignedSlots(
                  p.id,
                  assignments,
                  config.blocks,
                  config.timeSlots,
                  coachesById
                );
                const assignedSummary =
                  assignedSlots.length > 0
                    ? assignedSlots.map((s) => s.start).join(', ')
                    : '—';
                const coachesSummary =
                  assignedSlots.length > 0
                    ? [...new Set(assignedSlots.map((s) => s.coachName).filter(Boolean))].join(', ') || '—'
                    : '—';
                const flyer = flyers.find((f) => f.id === p.id);
                const hasWaterThur = !!(flyer?.manifestTickets && flyer.manifestTickets[WATER_THUR_LABEL] > 0);
                const hasCanopyThur = !!(flyer?.manifestTickets && flyer.manifestTickets[CANOPY_THUR_LABEL] > 0);
                const thuTraining =
                  hasWaterThur && hasCanopyThur
                    ? 'W+C'
                    : hasWaterThur
                    ? 'Water'
                    : hasCanopyThur
                    ? 'Canopy'
                    : '—';
                const refSummary = buildParticipantReferenceSummary(flyer);
                const refTitle =
                  refSummary ||
                  'No extra profile row — open ref for phone, height, weight, jumps, tunnel';
                const displayName = getParticipantDisplayName(p, flyer);
                const displayEmail = getParticipantDisplayEmail(p, flyer);
                return (
                  <div key={p.id} className="fury-lab-participant-wrap">
                    <div className="fury-lab-participant-row fury-lab-participant-row--main">
                      <div className="fury-lab-participant-name-cell">
                        <button
                          type="button"
                          className={`fury-lab-ref-toggle${participantDetailOpen[p.id] ? ' fury-lab-ref-toggle--open' : ''}`}
                          onClick={() => toggleParticipantDetail(p.id)}
                          aria-expanded={!!participantDetailOpen[p.id]}
                          aria-label={`Reference details for ${displayName !== '—' ? displayName : 'participant'}`}
                          title={refTitle}
                        >
                          Ref
                        </button>
                        <span
                          className="fury-lab-participant-readonly"
                          title={displayName !== '—' ? displayName : undefined}
                        >
                          {displayName}
                        </span>
                      </div>
                      <span
                        className="fury-lab-participant-readonly"
                        title={displayEmail !== '—' ? displayEmail : undefined}
                      >
                        {displayEmail}
                      </span>
                      <label className="fury-lab-meet-time-label">
                        <select
                          className="fury-lab-meet-time-select"
                          value={p.meetTime || ''}
                          onChange={(e) => updateParticipantMeetTime(p.id, e.target.value)}
                        >
                          <option value="">—</option>
                          {QUARTER_HOUR_OPTIONS.map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </label>
                      <span className="fury-lab-assigned-times" title="Assigned block start times">
                        {assignedSummary}
                      </span>
                      <span className="fury-lab-participant-coaches" title="Coaches for assigned blocks">
                        {coachesSummary}
                      </span>
                      <span className="fury-lab-thu-training" title="Water Training (Thur) / Canopy Thursday from manifest">
                        {thuTraining}
                      </span>
                      <span className="fury-lab-email-status">
                        {assignmentByParticipant.get(p.id)?.initialEmailSentAt ? 'Init' : '—'}
                        {' / '}
                        {assignmentByParticipant.get(p.id)?.reminderEmailSentAt ? 'Rem' : '—'}
                      </span>
                    </div>
                    {participantDetailOpen[p.id] && (
                      <div className="fury-lab-participant-detail">
                        {refSummary ? (
                          <span className="fury-lab-participant-detail-text">{refSummary}</span>
                        ) : (
                          <span className="fury-lab-participant-detail-muted">
                            No phone, height, weight, jumps, or tunnel on file for this flyer.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        </>
      )}

      {saving && <p className="fury-lab-saving">Saving…</p>}

      {furyExport && (
        <AdminColumnExportModal
          mode={furyExport.mode}
          columns={getFuryExportColumns(furyExport.kind)}
          selectedIds={furyExportCols}
          onChangeSelected={setFuryExportCols}
          onCancel={() => setFuryExport(null)}
          onConfirm={runFuryExport}
          onResetDefaults={resetFuryExportCols}
        />
      )}

      <EmailComposerModal
        isOpen={emailComposerProps.isOpen}
        context={emailComposerProps.context}
        initialRecipients={emailComposerProps.initialRecipients}
        initialTemplate={emailComposerProps.initialTemplate}
        initialSubject={emailComposerProps.initialSubject}
        initialBody={emailComposerProps.initialBody}
        onClose={closeEmailComposer}
        onEmailSent={handleEmailSent}
      />
    </div>
  );
}

export default FuryLab;
